// Opt-in, mounted reads only. One scheduler/listener set per browser, one flight
// per identity + resource key. No commands, seen markers or persistent storage.
export const FRESHNESS_INTERVAL = 15_000;
const REQUEST_TIMEOUT = 20_000;
const loading = () => ({ status: 'loading', data: null, error: null, isStale: true });

export function createFreshnessScheduler({
  window: win = globalThis.window, document: doc = globalThis.document,
  online = () => globalThis.navigator?.onLine !== false,
  now = () => Date.now(), setTimer = setTimeout, clearTimer = clearTimeout,
} = {}) {
  const entries = new Map();
  let timer = null;
  const available = () => doc?.visibilityState !== 'hidden' && online();
  let wasAvailable = available();
  const emit = (entry, state) => {
    entry.state = state;
    for (const subscriber of entry.subscribers) subscriber.notify();
  };
  function schedule() {
    clearTimer(timer);
    timer = null;
    if (!entries.size || !available()) return;
    const due = Math.min(...[...entries.values()].filter((e) => !e.retired && !e.flight).map((e) => e.next));
    if (!Number.isFinite(due)) return;
    timer = setTimer(tick, Math.max(0, due - now()));
  }
  function tick() {
    timer = null;
    if (available()) for (const entry of entries.values()) {
      if (entry.next <= now()) start(entry);
    }
    schedule();
  }
  function start(entry, manual = false) {
    if (entry.retired || entry.flight || !entry.subscribers.size) return;
    if (!available()) {
      emit(entry, { ...entry.state, isStale: true });
      return;
    }
    if (!manual && now() < entry.next) return;
    entry.queued = false;
    entry.next = now() + FRESHNESS_INTERVAL;
    const controller = new AbortController();
    const flight = { controller };
    entry.flight = flight;
    emit(entry, { ...entry.state, isStale: true });
    const valid = () => !entry.retired && entry.flight === flight && !controller.signal.aborted;
    const fail = (error) => {
      // Every failure closes the write gate and discards the prior payload,
      // including unauthorized/not-found responses. Never cache a stale grant.
      emit(entry, { status: 'error', data: null, error, isStale: true });
    };
    const timeout = setTimer(() => {
      if (!valid()) return;
      controller.abort();
      fail(new Error('최신 상태를 확인하지 못했습니다. 다시 연결해 주세요.'));
      // Keep the flight until the loader settles, even if it ignores abort.
    }, REQUEST_TIMEOUT);
    flight.timeout = timeout;
    const subscriber = entry.subscribers.values().next().value;
    Promise.resolve().then(() => valid() ? subscriber.load(controller.signal) : undefined).then((data) => {
      if (valid()) emit(entry, { status: 'ready', data, error: null, isStale: !available() });
    }, (error) => { if (valid()) fail(error); }).finally(() => {
      clearTimer(timeout);
      if (entry.flight === flight) entry.flight = null;
      if (!entry.retired) {
        if (entry.queued) {
          entry.next = now();
          start(entry, true);
        } else entry.next = Math.max(entry.next, now() + FRESHNESS_INTERVAL);
        schedule();
      }
    });
  }
  function reread(entry) {
    if (entry.retired) return;
    // Collapse reloads/command invalidations into one serialized follow-up.
    // An older read cannot reopen the write gate after a command completed.
    entry.queued = true;
    entry.flight?.controller.abort();
    emit(entry, { ...entry.state, isStale: true });
    entry.next = now();
    start(entry, true);
  }
  function reenter() {
    const isAvailable = available();
    const returned = isAvailable && !wasAvailable;
    wasAvailable = isAvailable;
    for (const entry of entries.values()) {
      if (entry.retired) continue;
      if (!isAvailable) {
        entry.flight?.controller.abort();
        emit(entry, { ...entry.state, isStale: true });
      } else if (returned) {
        // One availability transition bypasses the interval, using the existing
        // queue if the aborted loader still owns the flight.
        reread(entry);
      } else {
        // Focus/visibility/online bursts do not stale fresh data or queue another
        // read. A due start closes the gate itself; otherwise keep it usable.
        start(entry);
      }
    }
    schedule();
  }
  function listen(method) {
    doc?.[method]?.('visibilitychange', reenter);
    for (const event of ['focus', 'online', 'offline']) win?.[method]?.(event, reenter);
  }
  function retire(entry) {
    entry.retired = true;
    entry.flight?.controller.abort();
    clearTimer(entry.flight?.timeout);
    emit(entry, loading());
  }
  return {
    subscribe(id, key, load, notify) {
      let entry = entries.get(id);
      if (!entry) {
        entry = { key, state: loading(), subscribers: new Set(), next: 0, flight: null, retired: false };
        entries.set(id, entry);
        if (entries.size === 1) {
          wasAvailable = available();
          listen('addEventListener');
        }
      }
      const subscriber = { load, notify };
      entry.subscribers.add(subscriber);
      start(entry);
      schedule();
      return { get: () => entry.state,
        canWrite: () => !entry.retired && available() && entry.state.status === 'ready' && !entry.state.isStale,
        reload: () => { reread(entry); schedule(); },
        unsubscribe() {
          entry.subscribers.delete(subscriber);
          if (entry.subscribers.size) return;
          retire(entry);
          if (entries.get(id) === entry) entries.delete(id);
          if (!entries.size) listen('removeEventListener');
          schedule();
        },
      };
    },
    invalidate(matches) {
      for (const entry of entries.values()) if (matches(entry.key)) {
        reread(entry);
      }
      schedule();
    },
    clear() {
      for (const entry of entries.values()) retire(entry);
      entries.clear();
      clearTimer(timer);
      timer = null;
      listen('removeEventListener');
    },
  };
}

let scheduler;
export const freshnessScheduler = () => scheduler ??= createFreshnessScheduler();
